import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseVaultPage, VAULT_LIST_VIEW, VAULT_QUERY_PARAMS } from './vaultQueryParams';

/**
 * Profile pages (supplier, customer, employee, business) keep their documents view state in the
 * URL query, e.g. `/customer-profiles/12?workflow=Invoicing&page=2` or `?view=list&page=3`.
 * Every change of the profile tab / workflow / card-list view / page creates a history entry,
 * so the browser Back button from a document details page restores exactly the same view.
 */
export const PROFILE_QUERY_PARAMS = {
  TAB: 'tab',
  /** Legacy name of the tab param, still used by links from the profile lists. */
  LEGACY_TAB: 'initialTab',
  WORKFLOW: 'workflow',
  VIEW: VAULT_QUERY_PARAMS.VIEW,
  PAGE: VAULT_QUERY_PARAMS.PAGE,
  /** Added by the signing page "Back" link; it has no meaning on profile pages. */
  LEGACY_ACTIVE_TAB: VAULT_QUERY_PARAMS.LEGACY_ACTIVE_TAB,
} as const;

export type ProfileQueryState = {
  /** Index of the profile tab (Documents / Details / Contacts / Notes). */
  tab: number;
  /** Selected workflow card in the card view of the Documents tab. */
  workflow: string | null;
  cardView: boolean;
  page: number;
};

const parseTab = (value: string | null | undefined): number => {
  const tab = Number(value);
  return Number.isInteger(tab) && tab >= 0 ? tab : 0;
};

export const readProfileQuery = (params: URLSearchParams): ProfileQueryState => ({
  tab: parseTab(
    params.get(PROFILE_QUERY_PARAMS.TAB) ?? params.get(PROFILE_QUERY_PARAMS.LEGACY_TAB)
  ),
  workflow: params.get(PROFILE_QUERY_PARAMS.WORKFLOW) || null,
  cardView: params.get(PROFILE_QUERY_PARAMS.VIEW) !== VAULT_LIST_VIEW,
  page: parseVaultPage(params.get(PROFILE_QUERY_PARAMS.PAGE)),
});

/**
 * Returns a copy of `params` with the profile state written in its canonical form.
 * Defaults are omitted (tab 0, no workflow, card view, page 1). Unrelated params are preserved.
 */
export const writeProfileQuery = (
  params: URLSearchParams,
  state: ProfileQueryState
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(PROFILE_QUERY_PARAMS.LEGACY_TAB);
  next.delete(PROFILE_QUERY_PARAMS.LEGACY_ACTIVE_TAB);

  if (state.tab > 0) {
    next.set(PROFILE_QUERY_PARAMS.TAB, String(state.tab));
  } else {
    next.delete(PROFILE_QUERY_PARAMS.TAB);
  }

  if (state.workflow) {
    next.set(PROFILE_QUERY_PARAMS.WORKFLOW, state.workflow);
  } else {
    next.delete(PROFILE_QUERY_PARAMS.WORKFLOW);
  }

  if (state.cardView) {
    next.delete(PROFILE_QUERY_PARAMS.VIEW);
  } else {
    next.set(PROFILE_QUERY_PARAMS.VIEW, VAULT_LIST_VIEW);
  }

  if (state.page > 1) {
    next.set(PROFILE_QUERY_PARAMS.PAGE, String(state.page));
  } else {
    next.delete(PROFILE_QUERY_PARAMS.PAGE);
  }

  return next;
};

export type UpdateProfileQuery = (
  changes: Partial<ProfileQueryState>,
  options?: { replace?: boolean }
) => void;

/**
 * Reads / writes the profile documents view state from / to the URL.
 * Several components of one profile page may use it at the same time: every write starts from
 * the current browser URL, so writes never overwrite each other with stale values.
 *
 * @param normalize - rewrite legacy / invalid params in place (use it in one place per page).
 */
export const useProfileDocumentsQuery = (normalize = false) => {
  const [params, setSearchParams] = useSearchParams();
  const { tab, workflow, cardView, page } = useMemo(() => readProfileQuery(params), [params]);

  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updateProfileQuery: UpdateProfileQuery = useCallback(
    (changes, options) => {
      // Async callbacks (e.g. a finished request) must not change the URL of another page.
      if (!isMountedRef.current) {
        return;
      }
      const current = new URLSearchParams(window.location.search);
      const next = writeProfileQuery(current, { ...readProfileQuery(current), ...changes });
      if (next.toString() !== current.toString()) {
        setSearchParams(next, { replace: options?.replace ?? false });
      }
    },
    [setSearchParams]
  );

  /** Switching the profile tab resets the documents view to its defaults. */
  const setTab = useCallback(
    (nextTab: number) => updateProfileQuery({ tab: nextTab, workflow: null, page: 1 }),
    [updateProfileQuery]
  );

  /** Drop-in replacement for `useState<string | null>` used by the workflow cards. */
  const setWorkflow: Dispatch<SetStateAction<string | null>> = useCallback(
    (value: SetStateAction<string | null>) => {
      const current = readProfileQuery(new URLSearchParams(window.location.search));
      const nextWorkflow = typeof value === 'function' ? value(current.workflow) : value;
      updateProfileQuery({ workflow: nextWorkflow || null, page: 1 });
    },
    [updateProfileQuery]
  );

  useEffect(() => {
    if (!normalize) {
      return;
    }
    const canonical = writeProfileQuery(params, readProfileQuery(params));
    if (canonical.toString() !== params.toString()) {
      setSearchParams(canonical, { replace: true });
    }
  }, [normalize, params, setSearchParams]);

  return { tab, workflow, cardView, page, updateProfileQuery, setTab, setWorkflow };
};
